import type { OnDestroy, Injector, Signal } from '@angular/core'
import { computed, Injectable, inject } from '@angular/core'
import { UserStore } from '@awork/features/user/state/user.store'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import { User } from '@awork/features/user/models/user.model'
import type { Absence } from '@awork/features/user/models/absence.model'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { getLocalDateFromUTCString } from '@awork/_shared/functions/date-operations'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import { deepEqual } from '@awork/_shared/functions/lodash'
import { PermissionsQuery } from '@awork/features/workspace/state/permissions.query'
import type {
  DateRangeFilter,
  UserFilterOptions,
  UserRoleMapping,
  UserStatuses
} from '@awork/features/user/state/types'
import { UserColumn } from '@awork/features/user/state/types'

const USER_COLUMNS_ORDER: UserColumn[] = [
  UserColumn.Teams,
  UserColumn.Tags,
  UserColumn.Email,
  UserColumn.Position,
  UserColumn.Role,
  UserColumn.Capacity,
  UserColumn.Status
]

interface SearchFields {
  fullName: string
  firstName: string
  lastName: string
  teams: string
  role: string
  email: string
  tags: string
  position: string
}

@Injectable({ providedIn: 'root' })
export class UserQuery extends EntitySignalQuery<User> implements OnDestroy {
  protected store: UserStore
  private settingsQuery = inject(SettingsQuery)
  private permissionsQuery = inject(PermissionsQuery)

  static instance: UserQuery // Used to query the store in models
  private isResolvingCurrentUser = false

  constructor() {
    const store = inject(UserStore)

    super(store)
    this.store = store

    UserQuery.instance = this
  }

  /**
   * Clears the static reference when Angular destroys this query.
   */
  ngOnDestroy(): void {
    if (UserQuery.instance === this) {
      Reflect.deleteProperty(UserQuery, 'instance')
    }
  }

  /**
   * Checks PM eligibility using the workspace user, including partial project members.
   * @param {{ id: string; availableProducts?: ('pm' | 'ai')[] }} user User to check.
   * @param user.id
   * @param user.availableProducts
   * @returns {boolean} Whether PM selections may include this user.
   */
  hasPmProduct(user: { id: string; availableProducts?: ('pm' | 'ai')[] }): boolean {
    const workspaceUser = this.getEntity(user.id)
    return (workspaceUser ?? user).availableProducts?.includes('pm') === true
  }

  /**
   * Gets all available columns based on user permissions
   * @returns {UserColumn[]}
   */
  getAvailableColumns(): UserColumn[] {
    const isAdmin = this.isCurrentUserAdmin()

    if (!isAdmin) {
      return USER_COLUMNS_ORDER.filter(column => column !== UserColumn.Role)
    }

    return USER_COLUMNS_ORDER
  }

  /**
   * Gets the visible columns in list
   * @returns {Signal<UserColumn[]>}
   */
  queryColumns(): Signal<UserColumn[]> {
    const defaultLargeList = [
      UserColumn.Tags,
      UserColumn.Teams,
      UserColumn.Email,
      UserColumn.Position,
      UserColumn.Role,
      UserColumn.Capacity,
      UserColumn.Status
    ]

    return computed(() => {
      const isAdmin = this.isCurrentUserAdmin()

      const listColumns = this.settingsQuery.queryListColumns('user')
      let largeListColumns = (listColumns()?.largeList as UserColumn[]) || defaultLargeList

      // Filter out Role column for non-admin users
      if (!isAdmin) {
        largeListColumns = largeListColumns.filter(column => column !== UserColumn.Role)
      }

      return largeListColumns
    })
  }

  /**
   * Selects current user
   * @returns {Signal<User | null>}
   */
  queryCurrentUser(): Signal<User | null> {
    const user = this.queryActive()

    return computed<User | null>(() => user(), { equal: deepEqual })
  }

  /**
   * Selects the currently visited user profile
   * @returns {Signal<User | undefined>}
   */
  queryVisitedUser(): Signal<User | undefined> {
    return computed(
      () => {
        const visitedUserIdSignal = this.store.getVisitedUserId()
        const visitedUserId = visitedUserIdSignal()

        if (!visitedUserId) {
          return undefined
        }

        const visitedUser = this.queryUser(visitedUserId)

        return visitedUser()
      },
      { equal: deepEqual }
    )
  }

  /**
   * Gets current user
   * @returns {User}
   */
  getCurrentUser(): User {
    if (this.isResolvingCurrentUser) {
      return this.createFallbackUser()
    }

    this.isResolvingCurrentUser = true

    try {
      const currentUser = this.getActive()

      if (currentUser) {
        return this.mapEntity(currentUser)
      } else {
        // For the current user fallback I need to add some minimal information so it doesn't pops the onboarding screen
        return this.createFallbackUser()
      }
    } finally {
      this.isResolvingCurrentUser = false
    }
  }

  /**
   * Creates a fallback user used when no active user is available.
   * @returns {User}
   */
  private createFallbackUser(): User {
    return new User({ id: 'INVALID_ID', firstName: 'INVALID_NAME' }, 'UserQuery.getCurrentUser')
  }

  /**
   * Returns whether the current user is available.
   * @returns {boolean}
   */
  hasCurrentUser(): boolean {
    return !this.isResolvingCurrentUser && !!this.getActive()
  }

  /**
   * Selects user by id
   * @param {string} id
   * @param {Injector} injector
   * @returns {Observable<User>}
   */
  selectUser(id: string, injector: Injector): Observable<User> {
    return this.selectEntity(id, injector)
  }

  /**
   * Gets user by id
   * @param {string} id
   * @returns {Signal<User>}
   */
  queryUser(id: string): Signal<User> {
    return this.queryEntity(id)
  }

  /**
   * Gets user by id
   * @param {string} id
   * @returns {User}
   */
  getUser(id: string): User {
    const user = this.getEntity(id)

    return this.mapEntity(user)
  }

  /**
   * Selects users by id
   * @param {Injector} injector
   * @param {string[]} ids
   * @param {string} searchQuery
   * @param {boolean} [includeExternal=false]
   * @returns {Observable<User[]>}
   * @deprecated
   */
  selectUsersById(
    injector: Injector,
    ids: string[],
    searchQuery?: string,
    includeExternal = false
  ): Observable<User[]> {
    return this.selectAll(injector, {
      sortBy: (userA, userB) => this.getSort(userA, userB),
      filterBy: user =>
        ids.includes(user.id) && this.searchFilter(user, searchQuery) && this.filterExternalUsers(user, includeExternal)
    }).pipe(map(users => this.mapEntities(users)))
  }

  /**
   * Gets users by id
   * @param {string[]} ids
   * @param {boolean} [includeExternal=false]
   * @returns {User[]}
   */
  getUsersByIds(ids: string[], includeExternal = false): User[] {
    if (!Array.isArray(ids)) {
      return []
    }

    const users = this.getAll({
      sortBy: (userA, userB) => this.getSort(userA, userB),
      filterBy: user => ids.includes(user.id) && this.filterExternalUsers(user, includeExternal)
    })

    return this.mapEntities(users)
  }

  /**
   * Gets users by ids
   * @param {string[]} ids
   * @returns {Signal<User[]>}
   */
  queryUsersById(ids: string[]): Signal<User[]> {
    return computed(() => {
      const users = this.queryMany(ids)
      return users()
    })
  }

  /**
   * Gets all users
   * @param {boolean} [includeExternal=false]
   * @returns {User[]}
   */
  getAllUsers(includeExternal = false): User[] {
    const users = this.getAll({
      filterBy: user => this.filterExternalUsers(user, includeExternal),
      sortBy: (userA, userB) => this.getSort(userA, userB)
    })

    return this.mapEntities(users)
  }

  /**
   * Selects all users
   * @param {Injector} injector
   * @param {number} limit
   * @param {string} searchQuery
   * @param {boolean} [includeExternal=false]
   * @param {UserRoleMapping[]} roles
   * @returns {Observable<User[]>}
   * @deprecated -- use queryAllUsers instead
   * @deprecated
   */
  private selectAllUsers(
    injector: Injector,
    limit?: number,
    searchQuery?: string,
    includeExternal = false,
    roles?: UserRoleMapping[]
  ): Observable<User[]> {
    return this.selectAll(injector, {
      sortBy: (userA, userB) => this.getSort(userA, userB),
      filterBy: user => this.searchFilter(user, searchQuery, roles) && this.filterExternalUsers(user, includeExternal),
      limitTo: limit
    }).pipe(map(users => this.mapEntities(users)))
  }

  /**
   * Query all users using Signals
   * @param {number} limit
   * @param {string} searchQuery
   * @param {boolean} [includeExternal=false]
   * @param {UserRoleMapping[]} roles
   * @returns {Signal<User[]>} A signal containing the list of users.
   */
  queryAllUsers(
    limit?: number,
    searchQuery?: string,
    includeExternal = false,
    roles?: UserRoleMapping[]
  ): Signal<User[]> {
    const users = this.queryAll({
      sortBy: (userA: User, userB: User) => this.getSort(userA, userB),
      filterBy: (user: User) =>
        this.searchFilter(user, searchQuery, roles) && this.filterExternalUsers(user, includeExternal),
      limitTo: limit
    })

    return users
  }

  /**
   * Query all users using Signals
   * @param {{ limit?: number; searchQuery?: string; includeExternal?: boolean; excludeDeactivated?: boolean; roles?: UserRoleMapping[]; teamIds?: string[]; tagNames?: string[]; userIds?: string[]; }} [args={}]
   *   The arguments for querying users.
   * @returns {Signal<User[]>} A signal containing the list of users.
   * @param {number} [args.limit] - The maximum number of users to return.
   * @param {string} [args.searchQuery] - The search query to filter users.
   * @param {boolean} [args.includeExternal=false] - Whether to include external users.
   * @param {UserRoleMapping[]} [args.roles] - The roles to filter users.
   * @param {string[]} [args.teamIds] - The team IDs to filter users.
   * @param {string[]} [args.tagNames] - The tag names to filter users.
   * @param args.excludeDeactivated
   * @param {string[]} [args.userIds] - The user IDs to filter users.
   */
  queryPlannerUsers(
    args: {
      limit?: number
      searchQuery?: string
      includeExternal?: boolean
      excludeDeactivated?: boolean
      roles?: UserRoleMapping[]
      teamIds?: string[]
      tagNames?: string[]
      userIds?: string[]
    } = {}
  ): Signal<User[]> {
    const {
      limit,
      searchQuery = '',
      includeExternal = false,
      excludeDeactivated = true,
      roles = [],
      teamIds = [],
      tagNames = [],
      userIds = []
    } = args

    const users = this.queryAll({
      sortBy: (userA, userB) => this.getSort(userA, userB),
      filterBy: user =>
        user.availableProducts?.includes('pm') === true &&
        this.filterByTeamOrUserIds(user, { teamIds, userIds }) &&
        this.searchFilter(user, searchQuery, roles) &&
        this.filterExternalUsers(user, includeExternal) &&
        this.filterByTags(user, tagNames) &&
        this.filterByDeactivatedUsers(user, excludeDeactivated),
      limitTo: limit
    })

    return users
  }

  /**
   * Selects users by team
   * @param {string[]} teamIds
   * @param {number} limit
   * @returns {Signal<User[]>}
   */
  queryUsersByTeams(teamIds: string[], limit?: number): Signal<User[]> {
    const users = this.queryAll({
      sortBy: (userA, userB) => this.getSort(userA, userB),
      filterBy: user => this.filterByTeams(user, teamIds),
      limitTo: limit
    })

    return users
  }

  /**
   * Gets users by id
   * @param {string[]} ids
   * @param {boolean} [includeExternal=false]
   * @returns {Signal<User[]>}
   */
  queryUsersByIds(ids: string[], includeExternal = false): Signal<User[]> {
    const users = this.queryAll({
      filterBy: user => ids.includes(user.id) && this.filterExternalUsers(user, includeExternal)
    })

    return users
  }

  /**
   * Selects the users by status
   * @param {Injector} injector
   * @param {UserStatuses | UserStatuses[]} status
   * @param {number} limit
   * @param {string} searchQuery
   * @param {string[]} excludedUserIds
   * @param {boolean} [includeExternal=false]
   * @param {UserRoleMapping[]} roles
   * @returns {Observable<User[]>}
   * @deprecated
   */
  selectByStatus(
    injector: Injector,
    status?: UserStatuses | UserStatuses[],
    limit?: number,
    searchQuery?: string,
    excludedUserIds?: string[],
    includeExternal = false,
    roles?: UserRoleMapping[]
  ): Observable<User[]> {
    const query$ = status
      ? this.selectAll(injector, {
          filterBy: user => {
            return (
              this.statusFilter(user, status) &&
              this.searchFilter(user, searchQuery, roles) &&
              this.excludedUserFilters(user, excludedUserIds) &&
              this.filterExternalUsers(user, includeExternal)
            )
          },
          sortBy: (userA, userB) => this.getSort(userA, userB),
          limitTo: limit
        })
      : this.selectAllUsers(injector, limit, searchQuery)

    return query$.pipe(map(users => this.mapEntities(users)))
  }

  /**
   * Queries the users by status
   * @param {UserStatuses | UserStatuses[]} status
   * @param {number} limit
   * @param {string} searchQuery
   * @param {string[]} excludedUserIds
   * @param {boolean} [includeExternal=false]
   * @param {UserRoleMapping[]} roles
   * @returns {Signal<User[]>}
   */
  queryByStatus(
    status?: UserStatuses | UserStatuses[],
    limit?: number,
    searchQuery?: string,
    excludedUserIds?: string[],
    includeExternal = false,
    roles?: UserRoleMapping[]
  ): Signal<User[]> {
    const userQuery = status
      ? this.queryAll({
          filterBy: user => {
            return (
              this.statusFilter(user, status) &&
              this.searchFilter(user, searchQuery, roles) &&
              this.excludedUserFilters(user, excludedUserIds) &&
              this.filterExternalUsers(user, includeExternal)
            )
          },
          sortBy: (userA, userB) => this.getSort(userA, userB),
          limitTo: limit
        })
      : this.queryAllUsers(limit, searchQuery)

    return computed(() => {
      const users = userQuery()

      return this.mapEntities(users)
    })
  }

  /**
   * Gets the count of users
   * @param {UserStatuses | UserStatuses[]} status
   * @param {string} searchQuery
   * @param {User[]} filterUsers
   * @returns {number}
   */
  getUsersCount(status?: UserStatuses | UserStatuses[], searchQuery?: string, filterUsers?: User[]): number {
    return this.getCount(
      user =>
        this.statusFilter(user, status) &&
        this.searchFilter(user, searchQuery) &&
        this.filterExternalUsers(user, false) &&
        (!filterUsers || !filterUsers.filter(fu => !!fu).some(fu => fu.id === user.id))
    )
  }

  /**
   * Gets users with at least one absence (within the date range if set)
   * @param {DateRangeFilter} dateRange
   * @param {UserFilterOptions} options
   * @returns {Signal<User[]>}
   * @param {boolean} options.onlyActive
   * @param {boolean} options.includeExternal
   */
  queryUsersWithAbsences(dateRange?: DateRangeFilter, options?: UserFilterOptions): Signal<User[]> {
    const { includeExternal, usersToFilter } = options || {}
    const onlyActive = options?.onlyActive ?? true

    return computed(() => {
      const users = this.queryAll({
        filterBy: user =>
          !user.isArchived &&
          (!onlyActive || !user.isDeactivated) &&
          user.absences &&
          !!user.absences.length &&
          (!dateRange || user.absences.some(absence => this.isWithinDateRange(absence, dateRange))) &&
          this.filterExternalUsers(user, includeExternal) &&
          (!usersToFilter?.length || usersToFilter.includes(user.id))
      })

      return users().map(user => {
        user = new User(user, 'UserQuery.selectUsersWithAbsences')

        // Get only the absences within the date range
        if (dateRange) {
          user.absences = user.absences.filter(absence => this.isWithinDateRange(absence, dateRange))
        }

        return user
      })
    })
  }

  /**
   * Gets users assigned to a holiday region
   * @param {string} holidayRegionId
   * @param {boolean} [includeExternal=false]
   * @returns {User[]}
   */
  getUsersByHolidayRegion(holidayRegionId: string, includeExternal = false): User[] {
    const users = this.getAll({
      sortBy: (userA, userB) => this.getSort(userA, userB),
      filterBy: user => user.holidayRegion?.id === holidayRegionId && this.filterExternalUsers(user, includeExternal)
    })
    return this.mapEntities(users)
  }

  /**
   * Gets users assigned to a workspace absence region
   * @param {string} workspaceAbsenceId
   * @param {boolean} [includeExternal=false]
   * @returns {User[]}
   */
  getUsersByWorkspaceAbsence(workspaceAbsenceId: string, includeExternal = false): User[] {
    const users = this.getAll({
      sortBy: (userA, userB) => this.getSort(userA, userB),
      filterBy: user =>
        user.workspaceAbsence?.id === workspaceAbsenceId && this.filterExternalUsers(user, includeExternal)
    })

    return this.mapEntities(users)
  }

  // -------------------- Helper Functions -------------------- //

  /**
   * Checks if the absence dates are between dateRange
   * @param {Absence} absence
   * @param {DateRangeFilter} dateRange
   * @returns {boolean}
   */
  private isWithinDateRange(absence: Absence, dateRange: DateRangeFilter): boolean {
    absence.startOn = getLocalDateFromUTCString(absence.startOn)
    absence.endOn = getLocalDateFromUTCString(absence.endOn)

    return (
      absence.startOn.getTime() <= dateRange.endDate.getTime() &&
      absence.endOn.getTime() >= dateRange.startDate.getTime()
    )
  }

  /**
   * Filter used for quick status filters, specifies the minimum status a user has to have to be returned.
   * @param {User} user
   * @param {UserStatuses | UserStatuses[]} status
   * @returns {boolean}
   */
  private singleStatusFilter(user: User, status: UserStatuses | UserStatuses[]): boolean {
    switch (status) {
      case 'active':
        return user.status?.invitationAccepted && !user.isDeactivated
      case 'deactivated':
        return user.isDeactivated
      case 'invited':
        return !user.isDeactivated
      default:
        return true
    }
  }

  private statusFilter(user: User, status: UserStatuses | UserStatuses[]): boolean {
    if (Array.isArray(status)) {
      return status.some(s => this.singleStatusFilter(user, s))
    }

    return this.singleStatusFilter(user, status)
  }

  /**
   * Filter used for search by name
   * @param {User} user
   * @param {string} searchQuery
   * @param {UserRoleMapping[]} roles
   * @returns {boolean}
   */
  searchFilter(user: User, searchQuery: string, roles?: UserRoleMapping[]): boolean {
    if (!searchQuery) {
      return true
    }

    user = new User(user, 'UserQuery.searchFilter')

    const compareString = searchQuery.toLowerCase()
    const searchFields = this.prepareSearchFields(user, roles)

    if (searchQuery.length > 1) {
      return this.matchesLongQuery(searchFields, compareString)
    }

    return this.matchesShortQuery(searchFields, compareString)
  }

  /**
   * Prepares the search fields for the user
   * @param {User} user
   * @param {UserRoleMapping[]} roles
   * @returns {SearchFields}
   */
  private prepareSearchFields(user: User, roles?: UserRoleMapping[]): SearchFields {
    return {
      fullName: user.fullName?.toLowerCase(),
      firstName: user.firstName?.toLowerCase(),
      lastName: user.lastName?.toLowerCase(),
      teams: user.teams?.map(team => team?.name?.toLowerCase()).join(),
      role: roles ? roles?.find(role => role?.userId === user.id)?.name?.toLowerCase() : undefined,
      email: user.contactEmail?.toLowerCase(),
      tags: user.tags?.map(tag => tag?.name?.toLowerCase()).join(),
      position: user.position?.toLowerCase()
    }
  }

  /**
   * Checks if the user matches the search criteria.
   * @param {SearchFields} searchFields
   * @param {string} compareString
   * @returns {boolean}
   */
  private matchesLongQuery(searchFields: SearchFields, compareString: string): boolean {
    return (
      searchFields.fullName?.includes(compareString) ||
      searchFields.firstName?.includes(compareString) ||
      searchFields.lastName?.includes(compareString) ||
      searchFields.teams?.includes(compareString) ||
      searchFields.role?.includes(compareString) ||
      searchFields.email?.includes(compareString) ||
      searchFields.tags?.includes(compareString) ||
      searchFields.position?.includes(compareString)
    )
  }

  /**
   * Checks if the user matches the search criteria.
   * @param {SearchFields} searchFields
   * @param {string} compareString
   * @returns {boolean}
   */
  private matchesShortQuery(searchFields: SearchFields, compareString: string): boolean {
    return (
      searchFields.fullName?.startsWith(compareString) ||
      searchFields.firstName?.startsWith(compareString) ||
      searchFields.lastName?.startsWith(compareString)
    )
  }

  /**
   * Filter used to exclude specific users
   * @param {User} user
   * @param {string[]} userIds
   * @returns {boolean}
   * @private
   */
  private excludedUserFilters(user: User, userIds: string[]): boolean {
    if (!userIds) {
      return true
    } else {
      return !userIds.includes(user.id)
    }
  }

  /**
   * Returns whether the user should be filtered according to the includeExternal flag
   * @param {User} user
   * @param {boolean} includeExternal
   * @returns {boolean}
   */
  private filterExternalUsers(user: User, includeExternal: boolean): boolean {
    return includeExternal || !user.isExternal
  }

  /**
   * Returns whether the user should be filtered according to the isDeactivated flag
   * @param {User} user
   * @param {boolean} excludeDeactivated
   * @returns {boolean}
   */
  private filterByDeactivatedUsers(user: User, excludeDeactivated: boolean): boolean {
    if (excludeDeactivated) {
      return !user.isDeactivated
    }

    return true
  }

  /**
   * Filter users by teams
   * @param {User} user
   * @param {string[]} teamIds
   * @returns {boolean}
   */
  private filterByTeams(user: User, teamIds?: string[]): boolean {
    if (!teamIds?.length) {
      return true
    }

    return user.teams?.some(team => teamIds.includes(team.id)) || false
  }

  /**
   * Filter users by tags
   * @param {User} user
   * @param {string[]} tagNames
   * @returns {boolean}
   */
  private filterByTags(user: User, tagNames?: string[]): boolean {
    if (!tagNames?.length) {
      return true
    }

    return user.tags?.some(tag => tagNames.includes(tag.name)) ?? false
  }

  /**
   * Filter users by user IDs
   * @param {User} user
   * @param {string[]} userIds
   * @returns {boolean}
   */
  private filterByUserIds(user: User, userIds?: string[]): boolean {
    if (!userIds?.length) {
      return true
    }

    return userIds.includes(user.id)
  }

  /**
   * Filter users by team IDs or user IDs
   * @param {User} user The user to filter
   * @param {{ teamIds?: string[]; userIds?: string[]; }} filters The filters to apply
   * @returns {boolean}
   * @param {string[]} filters.teamIds - The team IDs to filter by
   * @param {string[]} filters.userIds - The user IDs to filter by
   */
  private filterByTeamOrUserIds(user: User, filters: { teamIds?: string[]; userIds?: string[] }): boolean {
    const { teamIds = [], userIds = [] } = filters

    if (!teamIds.length && !userIds?.length) {
      return true
    }

    if (!userIds.length && teamIds.length) {
      return this.filterByTeams(user, teamIds)
    }

    if (!teamIds.length && userIds.length) {
      return this.filterByUserIds(user, userIds)
    }

    return this.filterByUserIds(user, userIds) || this.filterByTeams(user, teamIds)
  }

  /**
   * Compares users by full name for a consistent list order.
   * @param {User} userA
   * @param {User} userB
   * @returns {number}
   */
  getSort(userA: User, userB: User): number {
    const userAFullName = `${userA.firstName || ''} ${userA.lastName || ''}`.trim()
    const userBFullName = `${userB.firstName || ''} ${userB.lastName || ''}`.trim()

    if (!userAFullName && userBFullName) {
      return 1
    }

    if (userAFullName && !userBFullName) {
      return -1
    }

    if (!userAFullName && !userBFullName) {
      return 0
    }

    return userAFullName.localeCompare(userBFullName)
  }

  /**
   * Checks if the current user is an admin
   * @returns {boolean}
   * @private
   */
  private isCurrentUserAdmin(): boolean {
    const userPermissions = this.permissionsQuery.getUserPermissions()
    return !!userPermissions?.isAdmin
  }
}
